import { getOperationalShiftState } from './operationalShiftState';
import {
  getCanonicalShiftStatus,
  getAssignedWorkerUid,
  getShiftStartMs,
  getShiftEndMs,
  hasWorkerAcceptanceEvidence,
  hasShiftArrivalEvidence,
  isTerminalShiftStatus,
} from "./shiftLifecycle";

function normalizeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

export function getShiftOperationalSummary(
  shift, now = Date.now()
) {
  if (!shift) {
    return {
      status: "open",

      assignedWorkerUid: "",
      assigned: false,

            accepted: false,
      awaitingAcceptance: false,

      arrived: false,
      enRoute: false,
      onSite: false,

      confirmedCoverage: false,
      terminal: false,

      hasValidTime: false,

      workerResponseStatus:
        "unassigned",

      assignmentLabel:
        "Unassigned",

      responseLabel:
        "Not Applicable",

          movementLabel:
        "Not Started",

      operationalLabel:
        "Open",

      timeLabel:
        "Time Missing",

      coverageType:
        "standard",
    };
  }

  const status =
    getCanonicalShiftStatus(
      shift
    ) || "open";

  const assignedWorkerUid =
    getAssignedWorkerUid(
      shift
    ) || "";

  const assigned =
    Boolean(
      assignedWorkerUid
    );

  const accepted =
    hasWorkerAcceptanceEvidence(
      shift
    );

  const arrived =
    hasShiftArrivalEvidence(
      shift
    );

  const terminal =
    isTerminalShiftStatus(
      status
    );

  const startMs =
    getShiftStartMs(
      shift
    );

  const endMs =
    getShiftEndMs(
      shift
    );

  const hasValidTime =
    Number.isFinite(
      startMs
    ) &&
    Number.isFinite(
      endMs
    ) &&
    startMs > 0 &&
    endMs > startMs;

  const rawWorkerResponseStatus =
    normalizeValue(
      shift?.workerResponseStatus
    );

  const workerResponseStatus =
    rawWorkerResponseStatus ||
    (accepted
      ? "accepted"
      : assigned
      ? "pending"
      : "unassigned");

   const coverageType =
    normalizeValue(
      shift?.coverageType ||
        shift?.mode ||
        shift?.assignMode ||
        "standard"
    );

  /*
   * Real-time operational interpretation.
   *
   * "assigned" alone does not mean confirmed
   * coverage. The worker must accept.
   */
  const awaitingAcceptance =
    status === "assigned" &&
    assigned &&
    !accepted;

  const enRoute =
    status === "tracking";

  const onSite =
    status === "arrived";

  const confirmedCoverage =
    (
      status === "assigned" &&
      assigned &&
      accepted
    ) ||
    [
      "activated",
      "tracking",
      "arrived",
      "completed",
    ].includes(status);

  let assignmentLabel =
    assigned
      ? "Assigned"
      : "Unassigned";

  if (
    status ===
    "closed_unfilled"
  ) {
    assignmentLabel =
      "Closed Unfilled";
  }

  let responseLabel =
    "Not Applicable";

  if (assigned) {
    if (
      accepted ||
      workerResponseStatus ===
        "accepted"
    ) {
      responseLabel =
        "Accepted";
    } else if (
      workerResponseStatus ===
        "declined"
    ) {
      responseLabel =
        "Declined";
    } else {
      responseLabel =
        "Pending";
    }
  }

  let movementLabel =
    "Not Started";

  if (
    status ===
      "tracking" ||
    shift?.tracking === true ||
    shift?.trackingStartedAt ||
    shift?.trackingAt ||
    shift?.onMyWayAt
  ) {
    movementLabel =
      "On My Way";
  }

  if (
    status ===
      "activated" &&
    movementLabel ===
      "Not Started"
  ) {
    movementLabel =
      "Activated";
  }

  if (arrived) {
    movementLabel =
      "Arrived";
  }

  if (
    status ===
    "completed"
  ) {
    movementLabel =
      "Completed";
  }

  if (
    status ===
    "no_show"
  ) {
    movementLabel =
      "No Show";
  }

  if (
    [
      "cancelled",
      "canceled",
    ].includes(status)
  ) {
    movementLabel =
      "Cancelled";
  }

    if (
    status ===
    "closed_unfilled"
  ) {
    movementLabel =
      "Closed Unfilled";
  }

  let operationalLabel =
    titleCase(status);

  if (status === "open") {
    operationalLabel =
      "Open";
  } else if (
    status === "time_required"
  ) {
    operationalLabel =
      "Time Required";
  } else if (
    status === "offered"
  ) {
    operationalLabel =
      "Offered";
  } else if (
    status === "requested"
  ) {
    operationalLabel =
      "Requested";
  } else if (
    status === "assigned"
  ) {
    operationalLabel =
      accepted
        ? "Accepted"
        : "Awaiting Acceptance";
  } else if (
    status === "activated"
  ) {
    operationalLabel =
      "Activated";
  } else if (
    status === "tracking"
  ) {
    operationalLabel =
      "En Route";
  } else if (
    status === "arrived"
  ) {
    operationalLabel =
      "Arrived";
  } else if (
    status === "completed"
  ) {
    operationalLabel =
      "Completed";
  } else if (
    status === "no_show"
  ) {
    operationalLabel =
      "No Show";
  } else if (
    [
      "cancelled",
      "canceled",
    ].includes(status)
  ) {
    operationalLabel =
      "Cancelled";
  } else if (
    status === "closed_unfilled"
  ) {
    operationalLabel =
      "Closed Unfilled";
  }

  const displayState = getOperationalShiftState(shift, now);
  if (displayState === "on_shift") operationalLabel = "On Shift";

  return {
    displayState,
    status,

    assignedWorkerUid,
    assigned,

        accepted,
    awaitingAcceptance,

    arrived,
    enRoute,
    onSite,

    confirmedCoverage,
    terminal,

    hasValidTime,

    workerResponseStatus,

    assignmentLabel,

    responseLabel,

       movementLabel,

    operationalLabel,

    timeLabel:
    
      hasValidTime
        ? "Time Confirmed"
        : "Time Missing",

    coverageType,

    statusLabel:
      titleCase(displayState),
  };
}